import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import styled from '@emotion/styled'
import { useTranslation } from 'react-i18next'
import {
  ChainProtocol,
  GrantKYCTokenFormData,
  OperationType,
  READABLE_KYC_STATUS,
  RevokeTraderKYCTokenFormData,
  TraderAddress,
} from '@archax/shared-types'
import { useMutation } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { ColDef, GridOptions, ICellRendererParams } from 'ag-grid-community'
import { Box, Button, Typography } from '@mui/material'
import { ServerSideGrid } from '../../../components/ServerSideGrid'
import { getPayoutWhitelist } from '../../../api/payouts'
import Dialog from '../../../components/Dialog/Dialog'
import { substringFilterParams } from '../../../util/common-grid-options'
import { createOperationForPayoutContract } from '../../../api/operations'
import onApiError from '../../../util/on-api-error'
import GrantKYCForm from '../components/GrantKYCForm'
import { AddButton } from '../../../components/ui/AddButton'

type KYCOperationData = {
  formData: GrantKYCTokenFormData | RevokeTraderKYCTokenFormData
  traderId: string
}

const StyledAddButton = styled(AddButton)(() => ({
  button: {
    '&:hover': {
      background: 'none',
    },
    div: {
      width: '20px',
      height: '20px',
      svg: {
        width: '18px',
        height: '18px',
      },
    },
  },
}))

const WhiteButton = styled(Button)(() => ({
  color: '#000',
  backgroundColor: '#fff',
  '&:hover': {
    color: '#fff',
    backgroundColor: '#0070B8',
  },
}))

type WhitelistProps = {
  protocol: ChainProtocol
}
export function Whitelist({ protocol }: WhitelistProps) {
  const { id } = useParams() as { id: string }
  const { t } = useTranslation()

  const [selectedRow, setSelectedRow] = useState<TraderAddress | null>(null)
  const [showGrantKYCDialog, setShowGrantKYCDialog] = useState(false)
  const [showRevokeKYCDialog, setShowRevokeKYCDialog] = useState(false)
  const [showGrantKYCFormDialog, setShowGrantKYCFormDialog] = useState(false)

  const { mutate: grantKYCMutation } = useMutation(
    (data: KYCOperationData) =>
      createOperationForPayoutContract(OperationType.PayoutContractGrantKyc, data.formData, {
        payoutContractId: id,
        traderId: data.traderId,
      }),
    {
      onSuccess: () => {
        toast.success(t('payoutContracts.showDetails.actions.pause.successMessage'))
      },
      onError: onApiError,
    },
  )

  const { mutate: revokeKYCMutation } = useMutation(
    (data: KYCOperationData) =>
      createOperationForPayoutContract(OperationType.PayoutContractRevokeKyc, data.formData, {
        payoutContractId: id,
        traderId: data.traderId,
      }),
    {
      onSuccess: () => {
        toast.success(t('payoutContracts.showDetails.actions.unpause.successMessage'))
      },
      onError: onApiError,
    },
  )

  const handleGrantKYC = async (row: TraderAddress) => {
    const formData = { traderAddressId: row.id }
    grantKYCMutation({ formData, traderId: row.trader.id })
    setShowGrantKYCDialog(false)
  }

  const handleRevokeKYC = async (row: TraderAddress) => {
    const formData = { traderAddressId: row.id }
    revokeKYCMutation({ formData, traderId: row.trader.id })
    setShowRevokeKYCDialog(false)
  }

  const columnDefs: ColDef<any>[] = useMemo(
    () => [
      {
        field: 'status',
        headerName: t('payoutContracts.whitelist.headers.status'),
        cellRenderer: (params: ICellRendererParams<any>) => {
          return params.data.isWhitelisted
            ? t('payoutContracts.whitelist.statuses.whitelisted')
            : t('payoutContracts.whitelist.statuses.removed')
        },
        width: 180,
        maxWidth: 180,
        flex: 1,
        sortable: false,
        filter: 'agSetColumnFilter',
        filterParams: {
          values: Object.values(READABLE_KYC_STATUS),
        },
      },
      {
        field: 'trader_name',
        headerName: t('payoutContracts.whitelist.headers.holderName'),
        cellRenderer: (params: ICellRendererParams<any>) => {
          const {
            traderAddress: { trader },
          } = params.data
          return trader.name
        },
        width: 300,
        maxWidth: 300,
        flex: 1,
        sortable: true,
        filter: 'agTextColumnFilter',
        filterParams: substringFilterParams,
      },
      {
        field: 'trader_address',
        headerName: t('payoutContracts.whitelist.headers.holderAddress'),
        cellRenderer: (params: ICellRendererParams<any>) => {
          const { traderAddress } = params.data
          return traderAddress.address
        },
        width: 400,
        flex: 1,
        sortable: true,
        filter: 'agTextColumnFilter',
        filterParams: substringFilterParams,
      },
      {
        field: '',
        headerName: '',
        cellRenderer: (params: ICellRendererParams<any>) => {
          const { isWhitelisted } = params.data
          return (
            <WhiteButton
              variant="contained"
              onClick={() => {
                setSelectedRow(params.data!.traderAddress)
                isWhitelisted ? setShowRevokeKYCDialog(true) : setShowGrantKYCDialog(true)
              }}
            >
              {isWhitelisted
                ? t('payoutContracts.whitelist.actions.revokeKyc.label')
                : t('payoutContracts.whitelist.actions.grantKyc.label')}
            </WhiteButton>
          )
        },
        width: 180,
        maxWidth: 180,
        flex: 1,
        sortable: false,
        filter: false,
        cellStyle: { display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 10 },
      },
    ],
    [t],
  )

  const gridOptions: GridOptions = useMemo(
    () => ({
      columnDefs,
      onGridReady({ api }) {
        api.sizeColumnsToFit()
      },
    }),
    [columnDefs],
  )

  return (
    <div>
      <Box
        sx={{ paddingLeft: 0 }}
        maxWidth="xl"
        flexDirection={'row'}
        display={'flex'}
        justifyContent={'space-between'}
        alignItems={'center'}
      >
        <Typography align="left" variant="h5">
          {t('payoutContracts.showDetails.tabs.whitelist')}
        </Typography>
        <Box display={'flex'} flexDirection={'row'} alignItems={'center'}>
          <StyledAddButton
            name={t('payoutContracts.whitelist.actions.grantKyc.message')}
            ariaLabel={t('payoutContracts.whitelist.actions.grantKyc.message')}
            onClick={() => setShowGrantKYCFormDialog(true)}
          />
        </Box>
      </Box>
      <Box sx={{ marginTop: 0 }}>
        <ServerSideGrid
          gridOptions={gridOptions}
          queryFn={(gridParams) => {
            return getPayoutWhitelist(id, { ...(gridParams as any) })
          }}
          queryParamsEnabled
          showOverlayLoading
          showRefreshIcon
        />
      </Box>
      <Dialog
        open={showGrantKYCDialog}
        title={t('payoutContracts.whitelist.actions.grantKyc.message')}
        onConfirm={() => handleGrantKYC(selectedRow as TraderAddress)}
        confirmLabel={t('payoutContracts.whitelist.actions.grantKyc.label')}
        onClose={() => setShowGrantKYCDialog(false)}
        showCancel
      >
        <Typography variant="body2">{t('payoutContracts.whitelist.actions.grantKyc.text')}</Typography>
      </Dialog>
      <Dialog
        open={showRevokeKYCDialog}
        title={t('payoutContracts.whitelist.actions.revokeKyc.message')}
        onConfirm={() => handleRevokeKYC(selectedRow as TraderAddress)}
        confirmLabel={t('payoutContracts.whitelist.actions.revokeKyc.label')}
        onClose={() => setShowRevokeKYCDialog(false)}
        showCancel
      >
        <Typography variant="body2">{t('payoutContracts.whitelist.actions.revokeKyc.text')}</Typography>
      </Dialog>
      {showGrantKYCFormDialog && (
        <GrantKYCForm
          payoutContractId={id}
          // eligibiltityTokenId={payoutDetails.tokenId}
          protocol={protocol}
          onClose={() => setShowGrantKYCFormDialog(false)}
          onConfirm={() => setShowGrantKYCFormDialog(false)}
        />
      )}
    </div>
  )
}
